/*
  market-notice.js — picks which of the pre-rendered, pre-translated notice
  blocks (snippets/market-notice.liquid) the visitor should see.

  Storefront HTML is CDN-cached and identical for everyone inside a
  (market, locale, currency) tuple, and Liquid has no visitor-IP variable, so
  the real country can only be found in the browser. All copy already sits in
  the page, hidden, translated by Shopify's own i18n pipeline — this file only
  removes a `hidden` attribute. It builds no strings on purpose: a string here
  would be untranslatable.

  Detection, in order:
    1. /browsing_context_suggestions.json -> detected_values.country.handle
       (Shopify's own geo-IP, same-origin, uncached)
    2. Intl timezone, for the three countries the notices care about
    3. nothing -> show nothing. A wrong banner is worse than no banner.

  States: GB -> gb bar. NO/CH -> slim customs bar. One of the other 27
  in-market countries -> nothing. Anything else -> the generic out-of-market
  bar. Unknown -> nothing.

  These bars are about where the VISITOR is, which is why geo-IP is the right
  input for them. The customs line above the checkout button is not: it is
  about where the parcel is going, so it is gated in Liquid on
  localization.country by snippets/cart-checkout-notices.liquid and this file
  has nothing to do with it.

  Add-to-cart is never disabled: the bar says it and checkout enforces it.
*/
(function () {
  'use strict';

  var IN_MARKET =
    'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE,NO,CH'.split(',');

  // Fallback only, used when Shopify's own geo-IP lookup fails.
  // Europe/Busingen is Büsingen am Hochrhein: a German exclave surrounded by
  // Switzerland. The country is DE, so it belongs to the 27 in-market EU
  // countries and sees no notice — snippets/delivery-promise.liquid maps the
  // same zone to DE and the two must not disagree.
  // Europe/Vaduz (Liechtenstein) is deliberately absent: LI is not in the
  // market, so it must fall through to the generic out-of-market bar rather
  // than be treated as Switzerland.
  var TIMEZONE_COUNTRY = {
    'Europe/London': 'GB',
    'Europe/Oslo': 'NO',
    'Europe/Zurich': 'CH',
    'Europe/Busingen': 'DE',
  };

  var STORAGE_KEY = {
    gb: 'rs_market_notice_v1',
    other: 'rs_market_notice_v1',
    noch: 'rs_customs_notice_v1',
  };

  var REMIND_AFTER = 30 * 24 * 60 * 60 * 1000; // dismissal lasts 30 days
  var NOCH_MIN_DELAY = 3000; // "a few seconds after arrival" when no popup runs
  var POPUP_POLL = 500;
  var POPUP_PENDING_DEADLINE = 30000; // stop waiting on a popup that never opens

  /* ---------- storage: never let a locked-down browser throw ---------- */

  function readStore(store, key) {
    try {
      return store.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeStore(store, key, value) {
    try {
      store.setItem(key, value);
    } catch (error) {
      /* private mode / storage disabled: the notice simply returns next visit */
    }
  }

  function isDismissed(key) {
    var stamp = parseInt(readStore(window.localStorage, key), 10);
    if (!stamp) return false;
    var age = Date.now() - stamp;
    return age >= 0 && age < REMIND_AFTER;
  }

  /* ---------- detection ---------- */

  function localeRoot() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (typeof root === 'string' && root.charAt(0) === '/') {
      return root.charAt(root.length - 1) === '/' ? root : root + '/';
    }
    return '/';
  }

  function countryFromTimezone() {
    try {
      var zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return TIMEZONE_COUNTRY[zone] || null;
    } catch (error) {
      return null;
    }
  }

  function detectCountry() {
    var url = localeRoot() + 'browsing_context_suggestions.json?country[enabled]=true&language[enabled]=true';

    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (data) {
        var detected = data && data.detected_values && data.detected_values.country;
        var handle = detected && typeof detected.handle === 'string' ? detected.handle.toUpperCase() : null;
        return handle || countryFromTimezone();
      })
      .catch(function () {
        // Endpoint blocked, offline, or an ad blocker ate it: fall back quietly.
        return countryFromTimezone();
      });
  }

  function stateFor(country) {
    if (!country) return null;
    if (country === 'GB') return 'gb';
    if (country === 'NO' || country === 'CH') return 'noch';
    return IN_MARKET.indexOf(country) === -1 ? 'other' : null;
  }

  /* ---------- the promo popup gets the screen first ---------- */

  function popupBusy() {
    var popup = document.querySelector('promo-popup');
    if (!popup) return false;

    var dialog = popup.querySelector('dialog');
    if (dialog && dialog.hasAttribute('open')) return true;

    // The popup stamps sessionStorage the moment it reveals and localStorage
    // once the visitor has subscribed; either stamp means it is done with the
    // screen. Otherwise it is still counting down.
    if (readStore(window.localStorage, 'ch-promo-popup-done')) return false;
    if (readStore(window.sessionStorage, 'ch-promo-popup-dismissed')) return false;
    return true;
  }

  function whenScreenIsFree(callback) {
    var pendingUntil = Date.now() + POPUP_PENDING_DEADLINE;

    (function poll() {
      var popup = document.querySelector('promo-popup');
      var dialog = popup && popup.querySelector('dialog');
      var open = Boolean(dialog && dialog.hasAttribute('open'));

      // An open card is always waited out. A card that has not opened yet is
      // waited out only until the deadline, so a popup suppressed for some
      // other reason can never swallow the notice.
      if (open || (popupBusy() && Date.now() < pendingUntil)) {
        window.setTimeout(poll, POPUP_POLL);
        return;
      }
      callback();
    })();
  }

  /* ---------- rendering ---------- */

  function show(block, state) {
    block.hidden = false;

    block.addEventListener('click', function (event) {
      if (!event.target.closest('[data-market-notice-dismiss]')) return;
      block.hidden = true;
      writeStore(window.localStorage, STORAGE_KEY[state], String(Date.now()));
    });
  }

  function run(state) {
    if (isDismissed(STORAGE_KEY[state])) return;

    var block = document.querySelector('[data-market-notice="' + state + '"]');
    if (!block) return;

    if (state !== 'noch') {
      show(block, state);
      return;
    }

    // Product pages float the sticky add-to-cart dock along the bottom edge.
    if (document.querySelector('sticky-atc')) {
      block.classList.add('market-notice--clear-dock');
    }

    window.setTimeout(function () {
      whenScreenIsFree(function () {
        show(block, state);
      });
    }, NOCH_MIN_DELAY);
  }

  if (!window.fetch || !document.querySelector('[data-market-notice-root]')) return;

  detectCountry()
    .then(function (country) {
      var state = stateFor(country);
      if (state) run(state);
    })
    .catch(function () {
      /* Whatever went wrong, a notice is optional chrome: stay silent. */
    });
})();
